/**
 * RegistrationDetail (C-1b) — a single registration: stage stepper, advance
 * actions (FSM), fields, and history. On Approval the linked Master Register
 * product is promoted to REGISTERED (backend).
 * Route: /regulatory/registrations/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from './Regulatory.module.css';
import {
  useRegistrationDetailQuery, useAdvanceRegistration,
  REGISTRATION_STAGES, REGISTRATION_FSM, STAGE_LABEL, CATEGORY_LABEL,
} from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

export function RegistrationDetail({ registrationId }: { registrationId: string }) {
  const q = useRegistrationDetailQuery(registrationId);
  const advance = useAdvanceRegistration(registrationId);
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading registration…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Registration not found. <Link to="/regulatory/registrations">Back to tracker</Link></div></main>;
  const r = q.data;
  const next = REGISTRATION_FSM[r.stage] ?? [];
  const curIdx = REGISTRATION_STAGES.indexOf(r.stage);

  async function doAdvance(to: string) {
    setMsg(null);
    try { await advance.mutateAsync({ toStage: to }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not advance the registration.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/regulatory/registrations" className={styles.crumb}>Registration Tracker</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{r.registrationCode}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{r.registrationCode}</span> · {r.productName}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{STAGE_LABEL[r.stage] ?? r.stage}</span>
            <span className={styles.sub}>{CATEGORY_LABEL[r.category] ?? r.category} · {r.authority} · {r.market}</span>
          </div>
        </div>
        <div className={styles.actions}>
          {next.length === 0 ? <span className={styles.terminal}>Terminal stage</span> : next.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${to === 'WITHDRAWN' ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doAdvance(to)} disabled={advance.isPending}>→ {STAGE_LABEL[to] ?? to}</button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      {/* Stage stepper (Concept → … → Launched). */}
      <nav className={styles.stepper} aria-label="Registration workflow">
        {r.stage === 'WITHDRAWN' ? (
          <span className={styles.stepWithdrawn}>Withdrawn — registration terminated</span>
        ) : REGISTRATION_STAGES.map((s, i) => {
          const state = i < curIdx ? 'done' : i === curIdx ? 'current' : 'future';
          return (
            <span key={s} className={`${styles.step} ${state === 'done' ? styles.stepDone : state === 'current' ? styles.stepCurrent : styles.stepFuture}`} aria-current={state === 'current' ? 'step' : undefined}>
              <span className={styles.stepDot} aria-hidden>{state === 'done' ? '✓' : i + 1}</span>
              <span className={styles.stepLabel}>{STAGE_LABEL[s]}</span>
            </span>
          );
        })}
      </nav>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Registration detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Dosage form</dt><dd>{r.dosageForm ?? '—'}</dd></div>
          <div><dt>Strength</dt><dd>{r.strength ?? '—'}</dd></div>
          <div><dt>Market</dt><dd>{r.market}</dd></div>
          <div><dt>Authority</dt><dd>{r.authority}</dd></div>
          <div><dt>Submission date</dt><dd>{fmtDate(r.submissionDate)}</dd></div>
          <div><dt>Target approval</dt><dd>{fmtDate(r.targetApprovalDate)}</dd></div>
          <div><dt>Approval date</dt><dd>{fmtDate(r.approvalDate)}</dd></div>
          <div><dt>Linked product</dt><dd>{r.productId
            ? <Link to="/regulatory/products/$id" params={{ id: r.productId }} className={styles.crumb}>View in register</Link>
            : '—'}</dd></div>
        </dl>
        {r.notes && <div className={styles.descBlock}><strong>Notes</strong><p>{r.notes}</p></div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(r.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.from_stage ? `${STAGE_LABEL[h.from_stage] ?? h.from_stage} → ${STAGE_LABEL[h.to_stage ?? ''] ?? h.to_stage}` : (STAGE_LABEL[h.to_stage ?? ''] ?? h.event_kind.replaceAll('_', ' ').toLowerCase())}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
